import {Modal, Text} from '@forecast-it/design-system';
import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from './generic_modal_conductor';
import {ModalProps} from './types';

export type CascadeHeldTasksModalPayload = {
	/** Relay ids of the completed tasks a cascade declined to move. */
	heldTaskIds: string[];
	/** Called when the user agrees to move them. Receives the same ids. */
	onConfirm: (heldTaskIds: string[]) => void;
};

export const showCascadeHeldTasksModal = (props: CascadeHeldTasksModalPayload): void => {
	showModal({
		type: MODAL_TYPE.CASCADE_HELD_TASKS,
		...props,
	});
};

/**
 * Asks whether completed tasks should move too.
 *
 * A date change pushes everything downstream that can move, but stops at finished
 * work rather than rewriting it silently. The edit has already committed by the
 * time this appears, so declining is a complete, consistent outcome and not a
 * half-applied state - everything below a task left alone was recomputed against
 * that task's unchanged dates.
 */
export const CascadeHeldTasksModal = ({
	closeModal,
	heldTaskIds,
	onConfirm,
}: ModalProps<CascadeHeldTasksModalPayload>): JSX.Element => {
	const intl = useIntl();
	const submitted = useRef(false);

	const handleConfirm = () => {
		// A ref rather than state, because closeModal() unmounts this component in the
		// same tick: a state setter would not have been read back before the second
		// press, and there is no re-render to disable the button on either. A ref is
		// written synchronously, so a second press in that same tick genuinely stops
		// here.
		//
		// Belt and braces, mind - the confirm is idempotent server-side. A second pass
		// finds the tasks already clear of their floor and plans nothing.
		if (submitted.current) {
			return;
		}
		submitted.current = true;
		onConfirm(heldTaskIds);
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{intl.formatMessage({id: 'scheduling.cascade_held_tasks.title'})}</Modal.Title>
			<Modal.Content>
				<Text>{intl.formatMessage({id: 'scheduling.cascade_held_tasks.body'}, {count: heldTaskIds.length})}</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleConfirm} dataCy="cascade-move-completed-tasks">
					{intl.formatMessage({id: 'scheduling.cascade_held_tasks.confirm'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cascade-leave-completed-tasks">
					{intl.formatMessage({id: 'scheduling.cascade_held_tasks.decline'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
